import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { DragulaModule, DragulaService } from 'ng2-dragula';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { CoreModule } from '../core/core.module';
import { ActionProgressInfoComponent } from './action-progress-info/action-progress-info.component';
import { AutocompleteComponent } from './autocomplete/autocomplete.component';
import { BarcodeInputComponent } from './barcode-input/barcodeInput.component';
import { BarcodeComponent } from './barcode/barcode.component';
import { ButtonHideTableComponent } from './button-hide-table/button-hide-table.component';
import { CalendarDatepickerRangeComponent } from './calendar-datepicker-range/calendar-datepicker-range.component';
import { ChangeOrderStatusComponent } from './change-order-status/change-order-status.component';
import { ConfirmComponent } from './confirm/confirm.component';
import { DatepickerInputComponent } from './datepicker/datepicker-input/datepickerInput.component';
import { DatepickerModalComponent } from './datepicker/datepicker-modal/datepickerModal.component';
import { DatepickerRangeComponent } from './datepicker/datepicker-range/datepickerRange.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { DeleteModalComponent } from './delete-modal/delete-modal.component';
import { ExportComponent } from './export/export.component';
import { FormFieldErrorComponent } from './form/form-field-error/form-field-error.component';
import { EANValidator } from './form/validators/ean.directive';
import { SKUValidator } from './form/validators/sku.directive';
import { ImageLazyLoadComponent } from './image-lazy-load/image-lazy-load.component';
import { ImportLocationFromCsvComponent } from './import-location-from-csv/import-location-from-csv.component';
import { ImportOrderFromCsvComponent } from './import-order-from-csv/import-order-from-csv.component';
import { ImportProductsFromCsvComponent } from './import-products-from-csv/import-products-from-csv.component';
import { InfoCardFooterComponent } from './info-card/components/info-card-footer/info-card-footer.component';
import { InfoCardRowComponent } from './info-card/components/info-card-row/info-card-row.component';
import { InfoCardComponent } from './info-card/info-card.component';
import { InfoTooltipComponent } from './info-tooltip/info-tooltip.component';
import { InvalidEanScannedComponent } from './invalid-ean-scanned/invalid-ean-scanned.component';
import { LayoutZoomLevelComponent } from './layout-zoom-level/layout-zoom-level.component';
import { LocationPathEditorComponent } from './location-path-editor/location-path-editor.component';
import { LocationPickerComponent } from './location-picker/location-picker.component';
import { LocationSelectModalComponent } from './location-picker/location-select-modal/location-select-modal.component';
import { LogoutComponent } from './logout/logout.component';
import { MassactionDropdownComponent } from './massaction/massaction-dropdown/massaction-dropdown.component';
import { MassactionSelectAllComponent } from './massaction/massaction-select-all/massaction-select-all.component';
import { MassactionSelectByFiltersComponent } from './massaction/massaction-select-by-filters/massaction-select-by-filters.component';
import { MikuAutocompleteComponent } from './miku-autocomplete/miku-autocomplete.component';
import { MikuCalendarDatepickerComponent } from './miku-calendar-datepicker/miku-calendar-datepicker.component';
import { DateTileComponent } from './miku-date-picker/date-tile/date-tile.component';
import { MikuDatePickerComponent } from './miku-date-picker/miku-date-picker.component';
import { MikuScannerFieldComponent } from './miku-scanner-field/miku-scanner-field.component';
import { MikuSelectComponent } from './miku-select/miku-select.component';
import { AddActivityModalComponent } from './modals/add-activity-modal/add-activity-modal.component';
import { AddAllowedIpModalComponent } from './modals/add-allowed-ip-modal/add-allowed-ip-modal.component';
import { AddCommentModalComponent } from './modals/add-comment-modal/add-comment-modal.component';
import { AddDeliveryExpectedItemModalComponent } from './modals/add-expected-item/add-delivery-expected-item-modal.component';
import { AddInventoryCheckRequestModalComponent } from './modals/add-inventory-check-request-modal/add-inventory-check-request-modal.component';
import { AddProductDimensionsComponent } from './modals/add-product-dimensions/add-product-dimensions.component';
import { AddProductModalComponent } from './modals/add-product-modal/add-product-modal.component';
import { AddProductSeriesModalComponent } from './modals/add-product-series-modal/add-product-series-modal.component';
import { AddShortDateLevelComponent } from './modals/add-short-date-level/add-short-date-level.component';
import { AddWarehouseToProductComponent } from './modals/add-warehouse-to-product/add-warehouse-to-product.component';
import { ChangeShippingAddressModalComponent } from './modals/change-shipping-address-modal/change-shipping-address-modal.component';
import { ChoosePackageSizeModalComponent } from './modals/choose-package-size-modal/choose-package-size-modal.component';
import { ChooseQuantityModalComponent } from './modals/choose-quantity-modal/choose-quantity-modal.component';
import { ChooseSeriesModalComponent } from './modals/choose-series-modal/choose-series-modal.component';
import { CollectingActionsModalComponent } from './modals/collecting-actions-modal/collecting-actions-modal.component';
import { CreateLocationSizeModalComponent } from './modals/create-location-size-modal/create-location-size-modal.component';
import { CreatePackageSizeModalComponent } from './modals/create-package-size-modal/create-package-size-modal.component';
import { CreatePickingZoneModalComponent } from './modals/create-picking-zone-modal/create-picking-zone-modal.component';
import { CreateProdcutModalComponent } from './modals/create-prodcut-modal/create-prodcut-modal.component';
import { DecisionModalComponent } from './modals/decision-modal/decision-modal.component';
import { DeliveryVerifyItemModalComponent } from './modals/delivery/delivery-verify-item-modal/delivery-verify-item-modal.component';
import { DocumentListModalComponent } from './modals/document-list-modal/document-list-modal.component';
import { ActionsCellComponent } from './modals/document-list-modal/table-cells/actions-cell/actions-cell.component';
import { DocumentViewModalComponent } from './modals/document-view-modal/document-view-modal.component';
import { EditInvalidAddressModalComponent } from './modals/edit-invalid-address-modal/edit-invalid-address-modal.component';
import { HandlingPalletsActionModalComponent } from './modals/handling-pallets-action-modal/handling-pallets-action-modal.component';
import { InventoryCheckRequestModalComponent } from './modals/inventory-check-request-modal/inventory-check-request-modal.component';
import { LeaveProcessModalComponent } from './modals/leave-process-modal/leave-process-modal.component';
import { NewVersionModalComponent } from './modals/new-version-modal/new-version-modal.component';
import { NumberOfPackagesModalComponent } from './modals/number-of-packages-modal/number-of-packages-modal.component';
import { PackingActionsModalComponent } from './modals/packing-actions-modal/packing-actions-modal.component';
import { PackingSelectPackageModalComponent } from './modals/packing-select-package-modal/packing-select-package-modal.component';
import { ProductAddNewComponent } from './modals/product/product-add-new/product-add-new.component';
import { ProductRelatedOrdersModalComponent } from './modals/product/product-related-orders-modal/product-related-orders-modal.component';
import { ProgressBarComponent } from './modals/progress-bar/progress-bar.component';
import { QuantityModalComponent } from './modals/quantity-modal/quantity-modal.component';
import { RelatedItemsComponent } from './modals/related-items/related-items.component';
import { ReplenishPutAwayModalComponent } from './modals/replenish-put-away-modal/replenish-put-away-modal.component';
import { RequestToJoinComponent } from './modals/request-to-join/request-to-join.component';
import { ResponseToJoinComponent } from './modals/response-to-join/response-to-join.component';
import { ReturnAllOrderModalComponent } from './modals/return-all-order-modal/return-all-order-modal.component';
import { EditReturnCellComponent } from './modals/return-all-order-modal/tabl-cells/edit-return-cell/edit-return-cell.component';
import { ScanOrderNumberModalComponent } from './modals/scan-order-number-modal/scan-order-number-modal.component';
import { SelectItemModalComponent } from './modals/select-item-modal/select-item-modal.component';
import { SelectModalComponent } from './modals/shared/select-modal/select-modal.component';
import { ShowProductModalComponent } from './modals/show-product-modal/show-product-modal.component';
import { SpecialPackingTypeModalComponent } from './modals/special-packing-type-modal/special-packing-type-modal.component';
import { StayOnPageModalComponent } from './modals/stay-on-page-modal/stay-on-page-modal.component';
import { WriteSeriesAndQuantityComponent } from './modals/write-series-and-quantity/write-series-and-quantity.component';
import { MultiselectComponent } from './multiselect/multiselect.component';
import { NumberPickerComponent } from './number-picker/number-picker.component';
import { NumberSpinnerComponent } from './number-spinner/number-spinner.component';
import { PaginationComponent } from './pagination/pagination.component';
import { UcfirstPipe } from './pipes/ucfirst.pipe';
import { PreloadComponent } from './preload/preload.component';
import { ProductDetailsFormComponent } from './product-details-form/product-details-form.component';
import { BarcodeRangeComponent } from './range/barcodeRange.component';
import { RangeComponent } from './range/range.component';
import { BinarySelectComponent } from './select/binary-select/binary-select.component';
import { LevelSelectComponent } from './select/level-select/level-select.component';
import { SelectWarehouseComponent } from './select/warehouse/warehouse.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { CheckLocationMobileTableComponent } from './table-mobile-components/check-location-mobile-table/check-location-mobile-table.component';
import { CheckProductMobileTableComponent } from './table-mobile-components/check-product-mobile-table/check-product-mobile-table.component';
import { DefaultMobileTableComponent } from './table-mobile-components/default-mobile-table/default-mobile-table.component';
import { MonitoringMobileTableComponent } from './table-mobile-components/monitoring-mobile-table/monitoring-mobile-table.component';
import { PackingMobileTableHeaderComponent } from './table-mobile-components/packing/packing-mobile-table-header/packing-mobile-table-header.component';
import { WeightCellComponent } from './table/components/table-cells/weight-cell/weight-cell.component';
import { YesOrNoCellComponent } from './table/components/table-cells/yes-or-no-cell/yes-or-no-cell.component';
import { TableCustomComponentComponent } from './table/components/table-custom-component/table-custom-component.component';
import { TableHeadersComponent } from './table/components/table-headers/table-headers.component';
import { TableRowComponent } from './table/components/table-row/table-row.component';
import { TableComponent } from './table/table.component';
import { UploadButtonComponent } from './upload-button/upload-button.component';
import { UploadComponent } from './upload/upload.component';
import { VersionInfoComponent } from './version-info/version-info.component';
import { ProductVisualizationComponent } from './product-visualization/product-visualization.component';

@NgModule({
  providers: [DragulaService],
  imports: [
    FormsModule,
    RouterModule,
    NgSelectModule,
    CommonModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    ModalModule.forRoot(),
    TabsModule.forRoot(),
    MatButtonModule,
    CoreModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    MatListModule,
    MatTableModule,
    MatSelectModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    NgxTippyModule,
    DragulaModule,
    MatChipsModule,
    LazyLoadImageModule,
    NgOptimizedImage,
  ],
  declarations: [
    PreloadComponent,
    ConfirmComponent,
    PaginationComponent,
    DatepickerComponent,
    DatepickerInputComponent,
    DatepickerModalComponent,
    DatepickerRangeComponent,
    BarcodeInputComponent,
    ExportComponent,
    SelectWarehouseComponent,
    LocationPickerComponent,
    RangeComponent,
    LevelSelectComponent,
    BarcodeRangeComponent,
    SpinnerComponent,
    UcfirstPipe,
    EANValidator,
    BinarySelectComponent,
    SKUValidator,
    FormFieldErrorComponent,
    LogoutComponent,
    MikuDatePickerComponent,
    DateTileComponent,
    NumberPickerComponent,
    UploadButtonComponent,
    LocationPickerComponent,
    LocationSelectModalComponent,
    InvalidEanScannedComponent,
    MikuScannerFieldComponent,
    DeleteModalComponent,
    MikuAutocompleteComponent,
    MikuSelectComponent,
    ProductDetailsFormComponent,
    MikuCalendarDatepickerComponent,
    CalendarDatepickerRangeComponent,
    RequestToJoinComponent,
    ResponseToJoinComponent,
    WriteSeriesAndQuantityComponent,
    CreateProdcutModalComponent,
    AutocompleteComponent,
    RelatedItemsComponent,
    DecisionModalComponent,
    QuantityModalComponent,
    SelectItemModalComponent,
    LayoutZoomLevelComponent,
    ProgressBarComponent,
    ChangeOrderStatusComponent,
    MassactionSelectAllComponent,
    MassactionSelectByFiltersComponent,
    MassactionDropdownComponent,
    UploadComponent,
    ScanOrderNumberModalComponent,
    ProductRelatedOrdersModalComponent,
    ImportProductsFromCsvComponent,
    TableComponent,
    TableCustomComponentComponent,
    TableRowComponent,
    TableHeadersComponent,
    MonitoringMobileTableComponent,
    InfoTooltipComponent,
    NewVersionModalComponent,
    ButtonHideTableComponent,
    DefaultMobileTableComponent,
    PackingMobileTableHeaderComponent,
    PackingActionsModalComponent,
    ActionProgressInfoComponent,
    CollectingActionsModalComponent,
    ShowProductModalComponent,
    LocationPathEditorComponent,
    HandlingPalletsActionModalComponent,
    MultiselectComponent,
    DocumentListModalComponent,
    ActionsCellComponent,
    DocumentViewModalComponent,
    CreatePackageSizeModalComponent,
    ChoosePackageSizeModalComponent,
    AddActivityModalComponent,
    BarcodeComponent,
    CreatePackageSizeModalComponent,
    AddActivityModalComponent,
    ChoosePackageSizeModalComponent,
    NumberOfPackagesModalComponent,
    NumberSpinnerComponent,
    InfoCardComponent,
    InfoCardRowComponent,
    AddProductSeriesModalComponent,
    AddProductModalComponent,
    ImportOrderFromCsvComponent,
    InventoryCheckRequestModalComponent,
    ImportLocationFromCsvComponent,
    InfoCardFooterComponent,
    VersionInfoComponent,
    ReturnAllOrderModalComponent,
    EditReturnCellComponent,
    LeaveProcessModalComponent,
    AddDeliveryExpectedItemModalComponent,
    DeliveryVerifyItemModalComponent,
    ChangeShippingAddressModalComponent,
    EditInvalidAddressModalComponent,
    AddAllowedIpModalComponent,
    AddWarehouseToProductComponent,
    StayOnPageModalComponent,
    CheckLocationMobileTableComponent,
    AddInventoryCheckRequestModalComponent,
    SpecialPackingTypeModalComponent,
    AddCommentModalComponent,
    PackingSelectPackageModalComponent,
    ChooseQuantityModalComponent,
    ImageLazyLoadComponent,
    CheckLocationMobileTableComponent,
    CheckProductMobileTableComponent,
    AddShortDateLevelComponent,
    ProductAddNewComponent,
    SelectModalComponent,
    ChooseSeriesModalComponent,
    YesOrNoCellComponent,
    WeightCellComponent,
    CreateLocationSizeModalComponent,
    CreatePickingZoneModalComponent,
    AddProductDimensionsComponent,
    ReplenishPutAwayModalComponent,
    ProductVisualizationComponent,
  ],
  exports: [
    PreloadComponent,
    ConfirmComponent,
    PaginationComponent,
    DatepickerInputComponent,
    DatepickerRangeComponent,
    ExportComponent,
    SelectWarehouseComponent,
    LocationPickerComponent,
    RangeComponent,
    LevelSelectComponent,
    BarcodeRangeComponent,
    BarcodeInputComponent,
    SpinnerComponent,
    UcfirstPipe,
    EANValidator,
    BinarySelectComponent,
    SKUValidator,
    FormFieldErrorComponent,
    LogoutComponent,
    MikuDatePickerComponent,
    NumberPickerComponent,
    UploadButtonComponent,
    LocationPickerComponent,
    ProductDetailsFormComponent,
    MikuAutocompleteComponent,
    MikuSelectComponent,
    MikuCalendarDatepickerComponent,
    CalendarDatepickerRangeComponent,
    AutocompleteComponent,
    LayoutZoomLevelComponent,
    ChangeOrderStatusComponent,
    MassactionSelectAllComponent,
    MassactionSelectByFiltersComponent,
    MassactionDropdownComponent,
    UploadComponent,
    ScanOrderNumberModalComponent,
    ProductRelatedOrdersModalComponent,
    ImportProductsFromCsvComponent,
    TableComponent,
    TableRowComponent,
    MonitoringMobileTableComponent,
    ButtonHideTableComponent,
    ActionProgressInfoComponent,
    LocationPathEditorComponent,
    MultiselectComponent,
    InfoTooltipComponent,
    BarcodeComponent,
    MikuScannerFieldComponent,
    InfoCardComponent,
    InfoCardRowComponent,
    NumberSpinnerComponent,
    ImportOrderFromCsvComponent,
    ImportLocationFromCsvComponent,
    InfoCardFooterComponent,
    VersionInfoComponent,
    AddDeliveryExpectedItemModalComponent,
    DeliveryVerifyItemModalComponent,
    ImageLazyLoadComponent,
    WeightCellComponent,
    ReplenishPutAwayModalComponent,
  ],
  entryComponents: [DatepickerComponent, DatepickerModalComponent],
})
export class UtilsModule {}
